import { makeAutoObservable, observable } from 'mobx';
import type { TileDefinition } from '@/models/tiles/TileDefinition';
import type { LogicNodeDefinition } from '@/models/logic/LogicNodeDefinition';
import type { CircuitType } from '@/schemas/common';
import type { AppModel } from '@/models/AppModel';
import type { ChipExportBundle } from '@/schemas/chip';

export type ExtensionCapability = 'audio' | 'wireless' | 'network' | 'javascript' | 'mouse' | 'keyboard' | 'storage' | 'time';
export interface DigitilesExtension {
  id: string;
  name: string;
  version: string;
  description: string;
  capabilities?: readonly ExtensionCapability[];
  tiles?: readonly { definition: TileDefinition<any>; circuits: readonly CircuitType[] }[];
  nodes?: readonly LogicNodeDefinition<any>[];
  chips?: readonly { name: string; bundle: ChipExportBundle }[];
  /** Editor-scoped resources. No resources should start during module import or registration. */
  activate?: (app: AppModel, signal: AbortSignal) => (() => void);
}
export interface InstalledExtension {
  manifest: DigitilesExtension;
  paletteEnabled: boolean;
  runtimeEnabled: boolean;
  persistPreferences: boolean;
  error: string | null;
  paletteError: string | null;
}

const settingsKey = (id: string) => `digitiles-extension-settings:${id}`;
const capabilityKey = (manifest: DigitilesExtension) => [...(manifest.capabilities ?? [])].sort().join(',');

function loadPreferences(manifest: DigitilesExtension) {
  try {
    const saved = JSON.parse(localStorage.getItem(settingsKey(manifest.id)) ?? 'null');
    if (saved?.version === manifest.version && saved.capabilities === capabilityKey(manifest) &&
        typeof saved.paletteEnabled === 'boolean' && typeof saved.runtimeEnabled === 'boolean')
      return saved as { paletteEnabled: boolean; runtimeEnabled: boolean };
  } catch { /* Use defaults when storage is unavailable or malformed. */ }
  return null;
}

function savePreferences(entry: InstalledExtension) {
  if (!entry.persistPreferences) return;
  try {
    localStorage.setItem(settingsKey(entry.manifest.id), JSON.stringify({
      version: entry.manifest.version,
      capabilities: capabilityKey(entry.manifest),
      paletteEnabled: entry.paletteEnabled,
      runtimeEnabled: entry.runtimeEnabled,
    }));
  } catch { /* Keep the in-memory choice when storage is unavailable. */ }
}

/** Metadata changes are observable; definitions and hot simulation state are not. */
export class ExtensionCatalog {
  installed = new Map<string, InstalledExtension>();
  constructor() { makeAutoObservable(this, { installed: observable.shallow }, { autoBind: true }); }
  add(manifest: DigitilesExtension, persistPreferences = false) {
    const saved = persistPreferences ? loadPreferences(manifest) : null;
    this.installed.set(manifest.id, { manifest, paletteEnabled: saved?.paletteEnabled ?? false,
      runtimeEnabled: saved?.runtimeEnabled ?? false,
      persistPreferences, error: null, paletteError: null });
  }
  setPaletteEnabled(id: string, enabled: boolean) {
    const entry = this.installed.get(id);
    if (entry) {
      const updated = { ...entry, paletteEnabled: enabled };
      this.installed.set(id, updated);
      savePreferences(updated);
    }
  }
  setRuntimeEnabled(id: string, enabled: boolean) {
    const entry = this.installed.get(id);
    if (entry) {
      const updated = { ...entry, runtimeEnabled: enabled, error: entry.paletteError };
      this.installed.set(id, updated);
      savePreferences(updated);
    }
  }
  setError(id: string, error: string | null) {
    const entry = this.installed.get(id);
    if (entry && entry.error !== error) this.installed.set(id, { ...entry, error });
  }
  setPaletteError(id: string, error: string | null) {
    const entry = this.installed.get(id);
    if (entry) this.installed.set(id, { ...entry, error, paletteError: error });
  }
  tiles(circuit: CircuitType) {
    return [...this.installed.values()].flatMap(entry => entry.paletteEnabled && !entry.paletteError ? (entry.manifest.tiles ?? []).filter(tile => tile.circuits.includes(circuit)).map(tile => tile.definition.type) : []);
  }
}
export const extensionCatalog = new ExtensionCatalog();
